/**
 * Vorrat: was im Haus ist. Beim Übernehmen von Rezepten auf die Liste wird
 * der Vorrat abgezogen.
 */
import { useState } from 'react';
import { ApiError, api } from '../../api/client.ts';
import type { PantryItem } from '../../api/types.ts';
import { useCachedQuery } from '../../api/useCachedQuery.ts';
import { itemAmount, parseAmountWithUnit, parseQuickAdd } from '../../lib/shoppingFormat.ts';
import { texts } from '../../texts/de.ts';

/** Vorrat mit Eingabe und Bearbeitung der Mengen. */
export function PantryPanel() {
  const pantry = useCachedQuery<{ items: PantryItem[] }>('/api/pantry', 'einkauf.vorrat');
  const [text, setText] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const items = pantry.data?.items ?? [];

  /** Führt eine Änderung aus und übernimmt die neue Liste. */
  async function run(work: () => Promise<{ items: PantryItem[] }>) {
    setMessage(null);
    try {
      pantry.setData(await work());
    } catch (caught) {
      setMessage(caught instanceof ApiError ? caught.message : texts.common.error);
    }
  }

  return (
    <div>
      <form
        className="mb-4 flex gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          const parsed = parseQuickAdd(text);
          if (parsed === null) {
            return;
          }
          void run(() => api.post('/api/pantry', parsed));
          setText('');
        }}
      >
        <input
          value={text}
          onChange={(event) => setText(event.target.value)}
          placeholder={texts.shopping.pantryAddPlaceholder}
          aria-label={texts.shopping.quickAdd}
          className="flex-1 rounded-xl border border-border-subtle bg-surface-raised px-3 py-2.5"
        />
        <button
          type="submit"
          disabled={text.trim() === ''}
          className="rounded-xl bg-accent px-4 font-semibold text-on-accent disabled:opacity-50"
        >
          {texts.shopping.quickAdd}
        </button>
      </form>
      {message === null ? null : (
        <p className="mb-3 rounded-xl bg-accent-soft px-3 py-2 text-sm text-accent-strong">{message}</p>
      )}
      {pantry.offline ? <p className="mb-3 text-xs text-content-muted">{texts.shopping.offline}</p> : null}
      {items.length === 0 ? (
        <p className="rounded-card border border-border-subtle bg-surface-raised p-6 text-center text-sm text-content-muted">
          {texts.shopping.pantryEmpty}
        </p>
      ) : (
        <ul className="divide-y divide-border-subtle rounded-card border border-border-subtle bg-surface-raised px-3">
          {items.map((item) => (
            <li key={item.id} className="flex items-center gap-3 py-2 text-sm">
              <span className="flex-1">{item.name}</span>
              <input
                key={`${item.id}-${item.updatedAt}`}
                defaultValue={item.amount === null ? '' : itemAmount(item)}
                aria-label={`${texts.shopping.amount}: ${item.name}`}
                className="w-28 rounded-lg border border-border-subtle bg-surface px-2 py-1 text-right tabular-nums"
                onBlur={(event) => {
                  if (event.target.value.trim() === itemAmount(item)) {
                    return;
                  }
                  void run(() => api.put(`/api/pantry/${item.id}`, parseAmountWithUnit(event.target.value)));
                }}
              />
              <button
                type="button"
                onClick={() => void run(() => api.delete(`/api/pantry/${item.id}`))}
                aria-label={`${texts.shopping.remove}: ${item.name}`}
                className="rounded-lg px-2 py-1 text-content-muted hover:bg-surface-sunken"
              >
                ✕
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
